<template>
  <div class="skiers-container">
    <h1 class="title">
      All Skiers
      <span v-if="profileCount > 0" class="count-badge">{{ profileCount }}</span>
    </h1>
    
    <!-- Badge Legend -->
    <div class="badge-legend">
      <div class="legend-item">
        <img
          src="@/assets/verified.png"
          alt="Verified"
          class="legend-badge"
        />
        <span class="legend-text">= Pro Skier</span>
      </div>
    </div>

    <!-- Search Bar -->
    <div class="search-container">
      <div class="search-input-wrapper">
        <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <input
          type="text"
          v-model="searchQuery"
          @input="handleSearchInput"
          placeholder="Search skiers by name or username..."
          class="search-input"
        />
        <button
          v-if="searchQuery"
          @click="clearSearch"
          class="clear-button"
          title="Clear search"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
    
    <LoadingSpinner v-if="isLoading" />

    <div v-if="!isLoading && skiers.length" class="skiers-list">
      <div 
        v-for="skier in skiers" 
        :key="skier.id" 
        class="skier-card" 
        @click="navigateToProfile(skier.username)"
      >
        <img :src="skier.avatar || defaultAvatar" alt="Profile Picture" class="skier-avatar" />
        <div class="skier-info">
          <h2 class="skier-username">
            {{ skier.username }}
            <img
              v-if="skier.verified"
              src="@/assets/verified.png"
              alt="Verified"
              class="verified-badge"
              title="Verified Skier"
            />
            <router-link
              v-if="skier.ski_schools && skier.ski_schools.length > 0"
              :to="`/ski-school/${skier.ski_schools[0].slug}`"
              @click.stop
              class="ski-school-link"
            >
              <img
                :src="skier.ski_schools[0].logo"
                alt="Ski School"
                class="ski-school-badge"
                :title="`${skier.ski_schools[0].name} - ${skier.ski_schools[0].role}`"
              />
            </router-link>
            <span v-if="skier.verified" class="pro-badge"></span>
          </h2>
          <p class="skier-full-name">{{ skier.first_name }} {{ skier.last_name }}</p>
          <p class="skier-bio">{{ formatBio(skier.bio || '') }}</p>
        </div>
        <!-- Follow/Unfollow Button on the right side -->
        <button
          class="follow-button" 
          v-if="userID != null && userID !== skier.id"
          @click.stop="toggleFollow(skier)"
          @mouseenter="hoveredSkierId = skier.id"
          @mouseleave="hoveredSkierId = null"
          :class="{ 'unfollow': isFollowing(skier.id) }"
        >
          {{ getButtonText(skier.id) }}
        </button>
      </div>
    </div>

    <!-- Loading spinner for infinite scroll -->
    <div v-if="isFetching && !isLoading" class="infinite-scroll-loader">
      <LoadingSpinner />
    </div>

    <div v-else-if="!isLoading && !skiers.length">
      <p class="no-results">
        {{ isSearchMode ? `No skiers found matching "${searchQuery}".` : 'No skiers found.' }}
      </p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

export default {
  components: {
    LoadingSpinner,
  },
  data() {
    return {
      userID: null, // Store fetched user ID here
      skiers: [],
      profileCount: 0, // Add this line to store the count
      defaultAvatar: '/path/to/default/avatar.png', // Set path to default avatar
      isLoading: true, // Add a loading state
      following: [], // Add following list
      hoveredSkierId: null,
      offset: 0, // Track current offset
      limit: 20, // Number of skiers per fetch
      isFetching: false, // Prevent multiple simultaneous fetches
      allLoaded: false, // Track if all skiers are loaded
      searchQuery: '', // Search input value
      isSearchMode: false, // Track if we're in search mode
      searchDebounceTimer: null, // Timer for debouncing search
    };
  },
  async created() {
    await this.fetchUserID();
    await this.fetchFollowing();
    await this.fetchSkiers();
    await this.fetchProfileCount(); // Add this line
    this.isLoading = false; // Set loading to false after all data is fetched

    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
  },
  methods: {
    async fetchUserID() {
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          const response = await axios.get('https://api.6buoys.com/api/profile/', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          this.userID = response.data.id; // Store userID from API response
        } catch (error) {
          console.error('Error fetching user ID:', error);
        }
      }
    },
    async fetchFollowing() {
      try {
        const response = await axios.get(`https://api.6buoys.com/api/user-following/${this.userID}/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        // Simplify the following data structure to just store the IDs of followed users
        this.following = response.data.map(following => following.receiver);

      } catch (error) {
        console.error('Error fetching following:', error);
      }
    },
    async fetchSkiers(reset = false) {
      if (this.isFetching || (this.allLoaded && !reset)) return; // Prevent multiple fetches
      this.isFetching = true;
      
      if (reset) {
        this.offset = 0;
        this.allLoaded = false;
        this.skiers = [];
      }
      
      try {
        let response;
        
        if (this.isSearchMode && this.searchQuery.trim()) {
          // Use search endpoint
          response = await axios.get('https://api.6buoys.com/api/profiles/search/', {
            params: { 
              query: this.searchQuery.trim(),
              offset: this.offset, 
              limit: this.limit 
            },
          });
        } else {
          // Use regular profiles endpoint
          response = await axios.get('https://api.6buoys.com/api/profiles/', {
            params: { offset: this.offset, limit: this.limit },
          });
        }

        if (response.data.length < this.limit) {
          this.allLoaded = true; // Mark as all loaded if fewer results are returned
        }

        this.skiers = [...this.skiers, ...response.data]; // Append new skiers to the list
        this.offset += this.limit; // Increment the offset
      } catch (error) {
        console.error('Error fetching skiers:', error);
      } finally {
        this.isFetching = false;
      }
    },
    async fetchProfileCount() {
      try {
        const response = await axios.get('https://api.6buoys.com/api/profile-count/');
        this.profileCount = response.data.count;
      } catch (error) {
        console.error('Error fetching profile count:', error);
      }
    },
    handleSearchInput() {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      
      // Set new timer for debounced search
      this.searchDebounceTimer = setTimeout(() => {
        this.performSearch();
      }, 1500); 
    },
    async performSearch() {
      const query = this.searchQuery.trim();
      
      if (query === '') {
        // If search is empty, switch back to browse mode
        this.isSearchMode = false;
        await this.fetchSkiers(true); // Reset and fetch all skiers
      } else {
        // Switch to search mode and perform search
        this.isSearchMode = true;
        await this.fetchSkiers(true); // Reset and fetch search results
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.isSearchMode = false;
      this.fetchSkiers(true); // Reset and fetch all skiers
    },
    isFollowing(profileID) {
      // Check if the profileID exists in the following array
      return this.following.includes(profileID);
    },
    formatBio(bio) {
      return bio.length > 140 ? bio.substring(0, 140) + '...' : bio;
    },
    getButtonText(skierId) {
      if (this.isFollowing(skierId)) {
        return this.hoveredSkierId === skierId ? 'Unfollow' : 'Following';
      } else {
        return 'Follow';
      }
    },
    async toggleFollow(profile) {
      try {
        if (this.isFollowing(profile.id)) {
          await axios.delete(`https://api.6buoys.com/api/follow-delete/${this.userID}/${profile.id}/`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          // Remove the ID from following array
          this.following = this.following.filter(id => id !== profile.id);
        } else {
          await axios.post(`https://api.6buoys.com/api/follow-create/${this.userID}/${profile.id}/`, null, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
          });
          // Add the ID to following array
          this.following.push(profile.id);
        }
      } catch (error) {
        console.error('Error toggling follow:', error);
      }
    },
    navigateToProfile(username) {
      this.$router.push(`/${username}`);
    },
    handleScroll() {
      const scrollPosition = window.scrollY + window.innerHeight;
      const bottomPosition = document.documentElement.offsetHeight;

      if (scrollPosition >= bottomPosition - 200) { // Load more when 200px near the bottom
        this.fetchSkiers();
      }
    },
  },
};
</script>

<style scoped>
.skiers-container {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  background-color: #192734;
  border-radius: 16px;
  color: #d9d9d9;
}

@media screen and (max-width: 600px) {
  .skiers-container {
    padding: 20px 0px 56px 0px;
  }
}

.title {
  text-align: center;
  color: #1da1f2;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.count-badge {
  background-color: #1da1f2;
  color: white;
  border-radius: 20px;
  padding: 2px 12px;
  font-size: 0.6em;
  display: inline-block;
  vertical-align: middle;
  margin-bottom: -5px;
}

.badge-legend {
  text-align: center;
  margin-bottom: 20px;
  padding: 10px;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #8899a6;
  font-size: 0.9em;
}

.legend-badge {
  width: 18px;
  height: 18px;
}

.legend-text {
  color: #d9d9d9;
}

.search-container {
  margin-bottom: 24px;
  padding: 0 20px;
}

@media screen and (max-width: 600px) {
  .search-container {
    padding: 0 20px;
  }
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #15202b;
  border: 2px solid #1da1f2;
  border-radius: 12px;
  padding: 12px 16px;
  transition: all 0.2s ease;
}

.search-input-wrapper:focus-within {
  border-color: #1da1f2;
  box-shadow: 0 0 0 2px rgba(29, 161, 242, 0.2);
}

.search-icon {
  color: #8899a6;
  margin-right: 12px;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: #d9d9d9;
  font-size: 1rem;
  placeholder-color: #8899a6;
}

.search-input::placeholder {
  color: #8899a6;
}

.clear-button {
  background: none;
  border: none;
  color: #8899a6;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 8px;
}

.clear-button:hover {
  color: #d9d9d9;
  background-color: rgba(255, 255, 255, 0.1);
}

.skiers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}

.skier-card {
  display: flex;
  position: relative;
  background-color: #15202b;
  padding: 15px;
  border-radius: 12px;
  width: 100%;
  max-width: 350px;
  align-items: flex-start;
  gap: 15px;
  overflow: hidden;
  min-height: 100px;
  cursor: pointer;
}

.skier-avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #1da1f2;
  flex-shrink: 0;
}

.skier-info {
  flex-grow: 1;
  min-width: 0;
  padding-right: 70px;
}

.skier-username {
  margin: 0;
  font-size: 1.2em;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 5px;
}

.verified-badge {
  width: 18px;
  height: 18px;
}

.ski-school-badge {
  width: 18px;
  height: 18px;
  border-radius: 2px;
  object-fit: cover;
}

.pro-badge {
  color: #1da1f2;
  font-weight: 500;
  margin-left: -4px;
  font-size: 12px !important;
}

.skier-full-name {
  margin: 0px 0 0;
  color: #8899a6;
  font-size: 0.9em;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 40px;
  text-align: left;
}

.skier-bio {
  margin: 5px 0 0;
  color: #8899a6;
  font-size: 0.9em;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 40px;
  text-align: left;
}

.follow-button {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  background-color: #1da1f2;
  color: #fff;
  border: none;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
}

.follow-button.unfollow {
  background-color: transparent;
  border: solid white;
}

.follow-button.unfollow:hover {
  background-color: #ff4757;
  border: solid #ff4757;
  border: none;
}

.ski-school-link {
  text-decoration: none;
  color: inherit;
}

.infinite-scroll-loader {
  text-align: center;
  padding: 20px;
}

.no-results {
  text-align: center;
  color: #8899a6;
  font-style: italic;
  padding: 40px 20px;
}
</style>
